import React, { useState, useEffect, useCallback, useRef } from "react";
import "./EventCountdown.css";
import { parseEndTime, formatHMS, pickPhase } from "./logic";

/**
 * EventCountdown — a small fixed top-centered countdown to the event's end,
 * shown on the MiniMe first pages (home + capture/select), NOT on the edit
 * stage (the caller gates that). Client request, 2026-08-01: the photographer
 * gets a 5-minute post-end grace — the server keeps the attraction (and the
 * GPU) alive until end+grace, and this timer is how the photographer KNOWS
 * where they stand.
 *
 * Phases (remaining = scheduled end − now, device clock like the
 * Swingies/Minimove reference countdowns): calm >10m → warn 2–10m →
 * critical <2m → overtime (negative red blink, the grace window) → ended.
 *
 * Resync: endTime (+ graceMinutes/liteMode) is re-fetched every 60s, so an
 * admin end-time edit reaches an open app within ~a minute (the backend now
 * updates its cache immediately on a duration edit). Lite-mode events ignore
 * the schedule entirely (weeks-long from-home collection) — no countdown.
 * Fails soft: no endTime → renders nothing.
 */
const REFRESH_MS = 60000;

// Last successful fetch per event, at module scope: the component unmounts on
// every camera/edit transition inside CameraCapture, and without this seed it
// would render nothing after each remount until the refetch resolves (review
// finding 2026-08-01) — worst exactly on slow venue Wi-Fi during the grace.
const lastKnown = {};

const EventCountdown = ({ serverUrl, eventName }) => {
  const cached = lastKnown[eventName];
  const [endAt, setEndAt] = useState(cached ? new Date(cached.endMs) : null);
  const [graceSec, setGraceSec] = useState(cached ? cached.graceSec : 5 * 60);
  const [lite, setLite] = useState(cached ? cached.lite : false);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const endAtMsRef = useRef(cached ? cached.endMs : null);

  const fetchEndTime = useCallback(async () => {
    if (!serverUrl || !eventName) return;
    try {
      const resp = await fetch(
        `${serverUrl}/api/get-end_event_time?event_name=${encodeURIComponent(eventName)}`
      );
      if (!resp.ok) return;
      const data = await resp.json();
      const parsed = data.endTime ? parseEndTime(data.endTime) : null;
      if (parsed) {
        const grace =
          typeof data.graceMinutes === "number" ? data.graceMinutes * 60 : 5 * 60;
        const isLite = Boolean(data.liteMode);
        lastKnown[eventName] = { endMs: parsed.getTime(), graceSec: grace, lite: isLite };
        // Only touch state when the value actually changed (admin edit) —
        // avoids a pointless re-render churn every poll.
        if (endAtMsRef.current !== parsed.getTime()) {
          endAtMsRef.current = parsed.getTime();
          setEndAt(parsed);
        }
        setGraceSec(grace);
        setLite(isLite);
      }
    } catch {
      /* network blip — keep the last known end time */
    }
  }, [serverUrl, eventName]);

  useEffect(() => {
    fetchEndTime();
    const poll = setInterval(fetchEndTime, REFRESH_MS);
    return () => clearInterval(poll);
  }, [fetchEndTime]);

  useEffect(() => {
    if (!endAt || lite) return undefined;
    const tick = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(tick);
  }, [endAt, lite]);

  if (!endAt || lite) return null;

  const remaining = Math.round((endAt.getTime() - nowTick) / 1000);
  const phase = pickPhase(remaining, graceSec);

  return (
    <div className={`minime-event-countdown ${phase} rtl`} role="timer">
      {phase === "ended" && "האירוע הסתיים"}
      {phase === "overtime" && (
        // dir="ltr": a leading minus before digits inside an RTL context gets
        // bidi-flipped to the visual right ("01:23:45-") without it
        <span dir="ltr">{`-${formatHMS(-remaining)}`}</span>
      )}
      {phase !== "ended" && phase !== "overtime" && `נותרו ${formatHMS(remaining)}`}
    </div>
  );
};

export default EventCountdown;
